import { useCallback, useEffect, useState } from 'react'
import { ListChecks, Plus, Radio, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, EmptyState } from '@/components/States'
import { IdBar, LoadingCard, Row, useEntity } from './AdsDetailScreens'
import {
  getCampaign,
  listPlacements,
  addPlacement,
  deletePlacement,
  ADSD_STR as S,
  type CampaignDetail,
  type Placement,
} from './adsDetailApi'

/**
 * A20.08 (manage: list + add allow/block + delete) va A20.09 (remove: list +
 * delete) UMUMIY komponenti. Ikkalasi ham REAL orphan endpointlarni ulaydi:
 *   GET  /admin/ads/campaigns/:id/placements
 *   POST /admin/ads/campaigns/:id/placements {channel_ref,mode}
 *   DELETE /admin/ads/placements/:id
 */
function PlacementsManager({
  screenId,
  mode,
}: {
  screenId: 'A20.08' | 'A20.09'
  mode: 'manage' | 'remove'
}) {
  const { toast } = useToast()
  const camp = useEntity<CampaignDetail>(getCampaign)
  const [rows, setRows] = useState<Placement[] | null>(null)
  const [channel, setChannel] = useState('')
  const [addMode, setAddMode] = useState<'allow' | 'block'>('allow')
  const [busy, setBusy] = useState(false)

  const loadPlacements = useCallback(
    (id: number, signal?: AbortSignal) => {
      listPlacements(id, signal)
        .then((p) => setRows(p))
        .catch(() => setRows([]))
    },
    [],
  )

  useEffect(() => {
    if (camp.id === null || camp.id <= 0) {
      setRows(null)
      return
    }
    const ac = new AbortController()
    setRows(null)
    loadPlacements(camp.id, ac.signal)
    return () => ac.abort()
  }, [camp.id, camp.data, loadPlacements])

  const add = async () => {
    if (camp.id === null) return
    const v = channel.trim()
    if (!v) return
    setBusy(true)
    try {
      await addPlacement(camp.id, v, addMode)
      setChannel('')
      toast(S.done, { tone: 'success' })
      loadPlacements(camp.id)
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const remove = async (pid: number) => {
    if (camp.id === null) return
    try {
      await deletePlacement(pid)
      toast(S.done, { tone: 'success' })
      loadPlacements(camp.id)
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader
        title={mode === 'remove' ? S.placementRemove : S.placements}
        screenId={screenId}
        description={mode === 'remove' ? S.placementRemoveDesc : S.placementsDesc}
      />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <IdBar value={camp.idInput} onChange={camp.setIdInput} onSubmit={camp.submit} placeholder={S.campaignId} />
        {camp.id === null ? (
          <EmptyState
            icon={mode === 'remove' ? <Radio size={28} strokeWidth={1.5} /> : <ListChecks size={28} strokeWidth={1.5} />}
            title={S.enterId}
          />
        ) : camp.err ? (
          <ErrorState
            code={camp.err.code}
            title={camp.err.status === 404 ? S.notFound : undefined}
            onRetry={camp.reload}
          />
        ) : camp.loading || !camp.data ? (
          <LoadingCard />
        ) : (
          <div className="max-w-2xl space-y-4">
            <div className="rounded-lg border border-border-default bg-surface-2 p-4">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-base font-semibold text-primary">{camp.data.title}</h2>
                <span className="rounded-full border border-border-default px-2 py-0.5 text-xs text-secondary">
                  {camp.data.status}
                </span>
              </div>
              <Row label={S.advertiser}>{camp.data.advertiser}</Row>
            </div>

            {mode === 'manage' && (
              <div className="rounded-lg border border-border-default bg-surface-2 p-4">
                <label className="mb-1 block text-xs text-tertiary">{S.channelRef}</label>
                <div className="flex flex-wrap gap-2">
                  <input
                    value={channel}
                    onChange={(e) => setChannel(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && add()}
                    placeholder="@channel"
                    className="min-w-[12rem] flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
                  />
                  <select
                    value={addMode}
                    onChange={(e) => setAddMode(e.target.value as 'allow' | 'block')}
                    className="rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
                  >
                    <option value="allow">{S.allow}</option>
                    <option value="block">{S.block}</option>
                  </select>
                  <button
                    type="button"
                    onClick={add}
                    disabled={busy || !channel.trim()}
                    className="flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50"
                  >
                    <Plus size={14} />
                    {S.addChannel}
                  </button>
                </div>
              </div>
            )}

            <div className="overflow-hidden rounded-lg border border-border-default">
              <p className="border-b border-border-subtle bg-surface-2 px-4 py-2 text-xs font-medium text-secondary">
                {S.placements}
              </p>
              {rows === null ? (
                <div className="p-4 text-sm text-tertiary">…</div>
              ) : rows.length === 0 ? (
                <EmptyState title={S.empty} />
              ) : (
                rows.map((p, i) => (
                  <div
                    key={p.id}
                    className={`flex items-center justify-between px-4 py-2.5 ${i > 0 ? 'border-t border-border-subtle' : ''}`}
                  >
                    <div className="min-w-0">
                      <p className="truncate font-mono text-sm text-primary">{p.channel_ref}</p>
                      <p className="text-xs text-tertiary">
                        <span className={p.mode === 'block' ? 'text-danger' : 'text-emerald-500'}>
                          {p.mode === 'block' ? S.block : S.allow}
                        </span>{' '}
                        · {p.created_at}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(p.id)}
                      className="shrink-0 rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger"
                      title={S.delete}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

/* ── A20.08 — Kanal ro'yxati (joylashtirish) ────────────────────── */
export function PlacementsScreen() {
  return <PlacementsManager screenId="A20.08" mode="manage" />
}

/* ── A20.09 — Kanalni reklamadan chiqarish ──────────────────────── */
export function PlacementRemoveScreen() {
  return <PlacementsManager screenId="A20.09" mode="remove" />
}
